"use client";

import type { BestDecisionsResult } from "@beyourbest/decision-engine";
import Ball from "@Containers/game/builder/ball";
import BuilderDecisionOverlay from "@Containers/game/builder/builder-decision-overlay";
import {
  CARD_ACCENT_COLORS,
  DECISION_LINE_COLORS,
  DECISION_LINE_FALLBACK_COLOR,
  SNAPSHOT_PASS_LIMIT,
  SnapshotDecisionOption,
} from "@Containers/game/builder/builder-decision-utils";
import { getPlayer } from "@Containers/game/builder/functions/player-functions";
import { drawArrowhead, getPointAtTime } from "@Containers/game/builder/functions/utilities";
import Player from "@Containers/game/builder/player";
import TrackedBall from "@Containers/game/builder/tracked-ball";
import { Stage, goalZones } from "@Context/game/builder/builder-context";
import { useViewportLogic } from "@hooks/game/coach/360/builder/use-viewport";
import "@pixi/events";
import { Graphics as PixiGraphics } from "@pixi/graphics";
import { Container, Graphics, Sprite } from "@pixi/react";
import Dot2 from "@Resources/game/builder/dot2.png";
import Goal from "@Resources/game/builder/goal.png";
import SoccerField from "@Resources/game/builder/pitch_black_no_goals.jpg";
import { findPossessorAtTime, mapGameCoordsToScreen } from "@utilities/scenario-builder-replay";
import type { Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

interface ViewportProps {
  width: number;
  height: number;
  showTrackedBall?: boolean;
  showActionBall?: boolean;
  showRawTrackedBall?: boolean;
  showDecisionCards?: boolean;
  setShowDecisionCards?: Dispatch<SetStateAction<boolean>>;
  decisionSnapshot?: BestDecisionsResult | null;
  onAutoAssignDecisionScores?: () => Promise<{ status: "success" | "error"; message: string } | undefined> | void;
  autoAssignLoading?: boolean;
}

const Viewport = ({
  width,
  height,
  showTrackedBall,
  showActionBall,
  showRawTrackedBall,
  showDecisionCards = false,
  setShowDecisionCards,
  decisionSnapshot,
  onAutoAssignDecisionScores,
  autoAssignLoading,
}: ViewportProps) => {
  const shouldShowDecisionOverlay = Boolean(setShowDecisionCards || onAutoAssignDecisionScores || decisionSnapshot);
  const { state, viewportRef, handlePanStart, handlePan, handlePanEnd, onWheel, handleBallDragged, handleFieldClick } = useViewportLogic({ width, height });
  const [containerWidth, setContainerWidth] = useState(width);
  const [containerHeight, setContainerHeight] = useState(height - state.timelineHeight);

  const mapDecisionPitchPositionToScreen = useCallback(
    (position: { x: number; y: number }) => {
      const centeredPosition = {
        x: position.y - state.field.fieldWidth / 2,
        y: position.x - state.field.fieldDepth / 2,
      };

      return mapGameCoordsToScreen(centeredPosition, state.field);
    },
    [state.field]
  );

  const decisionOptions = useMemo(
    () =>
      (decisionSnapshot?.decisions.top ?? [])
        .filter(option => option.startPosition && option.endPosition && option.passType !== "shot" && option.passType !== "turnover")
        .slice(0, SNAPSHOT_PASS_LIMIT),
    [decisionSnapshot]
  );

  const liveDecisionStartPos = useMemo(() => {
    const possessorId = findPossessorAtTime({
      ballStates: state.ballStates,
      ballPoints: state.clip.scenario.ball.points,
      time: state.time,
      initialPossessor: state.clip.scenario.ball.initialPossessor,
    });

    const possessor = getPlayer(state.clip, possessorId);
    if (!possessor) {
      return null;
    }

    return getPointAtTime(possessor.points, state.time, possessor.beziers).position;
  }, [state.ballStates, state.clip, state.time]);

  const decisionLineData = useMemo(() => {
    return decisionOptions.map((option: SnapshotDecisionOption, index) => {
      const startPos = liveDecisionStartPos ?? mapDecisionPitchPositionToScreen(option.startPosition);
      const endPos = mapDecisionPitchPositionToScreen(option.endPosition);

      return {
        color: DECISION_LINE_COLORS[index] ?? DECISION_LINE_FALLBACK_COLOR,
        lineAlpha: index === 0 ? 0.95 : 0.7,
        lineWidth: (index === 0 ? 4 : 3) / state.mapScale,
        startPos,
        endPos,
      };
    });
  }, [decisionOptions, liveDecisionStartPos, mapDecisionPitchPositionToScreen, state.mapScale]);

  const decisionCards = useMemo(() => {
    return decisionOptions.map((option, index) => {
      return {
        accentColor: CARD_ACCENT_COLORS[index] ?? CARD_ACCENT_COLORS[0],
        index,
        option,
      };
    });
  }, [decisionOptions]);

  const drawDecisionLines = useCallback(
    (graphics: PixiGraphics) => {
      graphics.clear();
      if (!decisionLineData.length) return;

      decisionLineData.forEach(line => {
        graphics.lineStyle(line.lineWidth, line.color, line.lineAlpha);
        graphics.moveTo(line.startPos.x, line.startPos.y);
        graphics.lineTo(line.endPos.x, line.endPos.y);
        drawArrowhead(graphics, line.startPos, line.endPos, 10 / state.mapScale);
      });
    },
    [decisionLineData, state.mapScale]
  );

  useEffect(() => {
    if (viewportRef.current) {
      const updateDimensions = () => {
        const rect = viewportRef.current?.getBoundingClientRect();
        if (rect) {
          setContainerWidth(rect.width);
          setContainerHeight(rect.height);
        }
      };

      updateDimensions();

      const resizeObserver = new ResizeObserver(updateDimensions);
      resizeObserver.observe(viewportRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [width, height, state.timelineHeight]);

  return (
    <div
      ref={viewportRef}
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        overflow: "hidden",
        touchAction: "none",
      }}
      onPointerDown={handlePanStart}
      onPointerMove={handlePan}
      onPointerUp={handlePanEnd}
      onWheel={e => {
        onWheel(e);
      }}
    >
      <Stage
        width={containerWidth}
        height={containerHeight}
        options={{
          resolution: window.devicePixelRatio || 1,
          autoDensity: true,
          antialias: true,
          powerPreference: "high-performance",
          backgroundColor: 0xffffff,
        }}
      >
        <Container
          position={state.viewOffset}
          scale={state.mapScale}
          rotation={state.viewRotation}
          pivot={{ x: state.field.mapWidth / 2, y: state.field.mapHeight / 2 }}
        >
          <Sprite
            image={SoccerField}
            x={0}
            y={0}
            width={state.field.mapWidth}
            height={state.field.mapHeight}
            eventMode="static"
            onclick={handleFieldClick}
          />

          {goalZones.map((zone, index) => (
            <Sprite
              key={index}
              image={Goal}
              x={zone.x}
              y={zone.y}
              width={128}
              height={80}
              anchor={0.5}
              alpha={0.5}
              rotation={(index === 0 ? 1.5 : 0.5) * Math.PI}
            />
          ))}

          {Array.from(state.clip.scenario.players.values()).map(player => (
            <Player key={player.id} id={player.id} player={player} time={state.time} selected={player.id == state.selectedPlayer} />
          ))}

          {showActionBall && <Ball size={24} onDragged={handleBallDragged} />}
          {showRawTrackedBall && <TrackedBall color={0xffaa00} frames={state.clip.scenario.ball.debugPoints} size={24} opacity={0.5} />}
          {showTrackedBall && <TrackedBall color={0x00ffff} frames={state.clip.scenario.ball.points} size={24} showSegments />}
          {showDecisionCards && decisionLineData.length > 0 && <Graphics draw={drawDecisionLines} />}

          {state.showFieldRadial && (
            <Sprite
              image={Dot2}
              x={state.selectedPosition.x}
              y={state.selectedPosition.y}
              width={16 / state.mapScale}
              height={16 / state.mapScale}
              anchor={0.5}
            />
          )}
        </Container>
      </Stage>
      {shouldShowDecisionOverlay && (
        <BuilderDecisionOverlay
          decisionCards={decisionCards}
          showDecisionCards={showDecisionCards}
          setShowDecisionCards={setShowDecisionCards}
          onAutoAssignDecisionScores={onAutoAssignDecisionScores}
          autoAssignLoading={autoAssignLoading ?? false}
        />
      )}
    </div>
  );
};

export default Viewport;
